<template>
  <!-- Table Loading Overlay -->
  <div
    v-if="isLoading"
    class="fixed inset-0 bg-white z-50 flex items-center justify-center p-3 sm:p-4"
  >
    <div class="text-center max-w-xs sm:max-w-sm mx-auto">
      <div class="relative mb-3 sm:mb-4 md:mb-6">
        <!-- Main Spinner -->
        <div
          class="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 border-2 sm:border-4 md:border-[6px] border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"
        ></div>

        <!-- Inner Spinner -->
        <div
          class="absolute inset-0 w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 border-2 sm:border-4 md:border-[6px] border-transparent border-r-blue-400 rounded-full animate-spin mx-auto"
          style="animation-direction: reverse; animation-duration: 1.5s"
        ></div>
      </div>
      <div class="space-y-1 sm:space-y-2">
        <h3 class="text-base sm:text-lg md:text-xl font-semibold text-gray-800">
          {{ tableLoadingMessage }}
        </h3>
        <p class="text-xs sm:text-sm text-gray-600 px-2">
          Please wait while we load student data...
        </p>
        <!-- Loading Progress Dots -->
        <div class="flex justify-center space-x-1 mt-3 sm:mt-4">
          <div
            class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-600 rounded-full animate-pulse"
          ></div>
          <div
            class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-600 rounded-full animate-pulse"
            style="animation-delay: 0.1s"
          ></div>
          <div
            class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-600 rounded-full animate-pulse"
            style="animation-delay: 0.2s"
          ></div>
        </div>
      </div>
    </div>
  </div>

  <!-- main section -->
  <div
    class="bg-white rounded-lg border border-gray-200 overflow-hidden relative shadow-sm"
  >
    <!-- Table Header -->
    <div
      class="bg-gray-700 px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-gray-700"
    >
      <div class="flex items-center justify-between">
        <div class="flex-1 min-w-0">
          <h3
            class="text-base sm:text-lg md:text-xl font-bold text-white truncate"
          >
            Student Directory
          </h3>
          <p class="text-gray-300 text-xs sm:text-sm mt-0.5 sm:mt-1">
            Manage and view all student information
          </p>
        </div>
      </div>
    </div>
    <!-- Mobile View - Cards (< 768px) -->
    <div class="block md:hidden">
      <!-- Mobile Select All -->
      <div class="p-3 sm:p-4 border-b border-gray-200 bg-gray-50">
        <label class="flex items-center gap-2 sm:gap-3 cursor-pointer">
          <input
            type="checkbox"
            :checked="isAllSelected"
            @change="$emit('toggle-select-all')"
            :disabled="isLoading || students.length === 0"
            class="w-4 h-4 text-blue-500 bg-gray-100 border-gray-300 rounded focus:ring-blue-400 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <span class="text-sm font-medium text-gray-700">
            Select All Students
          </span>
        </label>
      </div>
      <!-- Mobile Skeleton Loading -->
      <div
        v-if="isLoading"
        class="p-3 sm:p-4 space-y-3 sm:space-y-4"
      >
        <div
          v-for="i in 5"
          :key="i"
          class="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3 shadow-sm"
        >
          <div class="flex items-center justify-between">
            <div
              class="h-3 sm:h-4 bg-gray-200 rounded w-16 sm:w-20 animate-pulse"
            ></div>
            <div
              class="h-5 sm:h-6 bg-gray-200 rounded-full w-12 sm:w-16 animate-pulse"
            ></div>
          </div>
          <div class="flex items-center space-x-2 sm:space-x-3">
            <div
              class="h-10 w-10 sm:h-12 sm:w-12 bg-gray-200 rounded-full animate-pulse flex-shrink-0"
            ></div>
            <div class="space-y-2 flex-1 min-w-0">
              <div
                class="h-3 sm:h-4 bg-gray-200 rounded w-24 sm:w-32 animate-pulse"
              ></div>
              <div
                class="h-2 sm:h-3 bg-gray-200 rounded w-20 sm:w-24 animate-pulse"
              ></div>
            </div>
          </div>
          <div class="flex justify-between">
            <div
              class="h-3 sm:h-4 bg-gray-200 rounded w-20 sm:w-24 animate-pulse"
            ></div>
            <div
              class="h-3 sm:h-4 bg-gray-200 rounded w-12 sm:w-16 animate-pulse"
            ></div>
          </div>
        </div>
      </div>
      <!-- Mobile Cards -->
      <div class="p-3 space-y-3 bg-gray-50">
        <div
          v-for="student in students"
          :key="student.id"
          class="group hover:shadow-md hover:border-blue-300 transition-all duration-200 touch-manipulation bg-white border border-gray-100 p-4 flex flex-col space-y-3 w-full max-w-2xl backdrop-blur rounded-2xl sm:rounded-3xl shadow-xl border-white/20"
          :class="{
            'ring-2 ring-blue-400 bg-blue-50/30 shadow-sm': isStudentSelected(
              student._id,
            ),
          }"
        >
          <!-- Header: Checkbox, Roll Number & Status -->
          <div class="flex items-center justify-between mb-3">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                :checked="isStudentSelected(student._id)"
                @change="$emit('toggle-student-selection', student._id)"
                :disabled="isLoading"
                class="w-4 h-4 text-blue-500 bg-gray-100 border-gray-300 rounded focus:ring-blue-400 focus:ring-2"
              />
              <span class="text-sm font-bold text-gray-800">
                <span>Roll Number:&nbsp;</span>{{ student.rollNumber }}
              </span>
            </label>
            <span
              :class="getStatusClass(student.status)"
              class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border"
            >
              <FileChartLine class="w-3 h-3" />
              {{ student.status === "leaved" ? "inactive" : student.status }}
            </span>
          </div>
          <!-- Student Profile -->
          <div class="flex items-center gap-3 mb-4">
            <div class="relative flex-shrink-0">
              <div
                @click="$emit('view-student', student)"
                class="h-12 w-12 rounded-xl flex items-center justify-center shadow-sm border border-blue-200 bg-blue-100"
              >
                <!-- If photo exists -->
                <img
                  v-if="student.photo"
                  :src="student.photo"
                  alt="Student Photo"
                  class="h-full w-full rounded-xl object-cover"
                />
                <!-- Fallback to initials -->
                <span
                  v-else
                  class="text-sm font-bold text-blue-600"
                >
                  {{ getInitials(student.name) }}
                </span>
              </div>

              <div
                class="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"
              ></div>
            </div>
            <div
              class="flex-1 min-w-0"
              @click="$emit('view-student', student)"
            >
              <h3 class="text-base font-bold text-gray-900 truncate mb-1">
                {{ student.name }}
              </h3>
              <div class="flex items-center gap-1 text-sm text-gray-600">
                <Mail class="w-3 h-3 text-gray-500" />
                <span class="truncate">{{ student.email }}</span>
              </div>
            </div>
          </div>
          <!-- Two-Line Info Layout -->
          <div
            class="space-y-2 mb-4"
            @click="$emit('view-student', student)"
          >
            <!-- Phone & Address Line -->
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <div
                  class="p-1.5 bg-green-50 rounded-lg border border-green-100"
                >
                  <Phone class="w-3 h-3 text-green-600" />
                </div>
                <span class="text-sm font-medium text-gray-700 truncate">{{
                  student.phone || "N/A"
                }}</span>
              </div>
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <div class="p-1.5 bg-blue-50 rounded-lg border border-blue-100">
                  <MapPinHouse class="w-3 h-3 text-blue-600" />
                </div>
                <span
                  class="text-sm text-gray-600 truncate"
                  :title="student.address || student.city"
                >
                  {{
                    student.address && student.address.trim()
                      ? student.address.includes(",")
                        ? student.address.split(",")[0]
                        : student.address.split(" ")[0]
                      : student.city && student.city.trim()
                      ? student.city
                      : "N/A"
                  }}
                </span>
              </div>
            </div>
            <!-- Class & Date Line -->
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <div
                  class="p-1.5 bg-purple-50 rounded-lg border border-purple-100"
                >
                  <School class="w-3 h-3 text-purple-600" />
                </div>
                <span class="text-sm font-medium text-gray-700 truncate">
                  <span>Class:&nbsp;</span>
                  {{
                    typeof student.class === "object"
                      ? `${student.class?.name || ""}${
                          student.class?.section
                            ? " - " +
                              student.class.section.replace(/section\s*/i, "")
                            : ""
                        }`
                      : student.class || "N/A"
                  }}
                </span>
              </div>
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <div
                  class="p-1.5 bg-orange-50 rounded-lg border border-orange-100"
                >
                  <Calendar class="w-3 h-3 text-orange-600" />
                </div>
                <span class="text-sm text-gray-600 truncate">{{
                  formatDate(student.enrollmentDate) || "N/A"
                }}</span>
              </div>
            </div>
          </div>
          <!-- Action Buttons -->
          <div
            class="flex items-center justify-end gap-2 pt-3 border-t border-gray-100"
          >
            <button
              @click="$emit('view-student', student)"
              :disabled="isLoading"
              class="flex items-center justify-center p-2 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-colors duration-200 border border-blue-200 hover:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
              title="View Details"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                ></path>
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                ></path>
              </svg>
            </button>
            <button
              @click="$emit('edit-student', student)"
              :disabled="isLoading"
              class="flex items-center justify-center p-2 text-green-600 hover:bg-green-600 hover:text-white rounded-lg transition-colors duration-200 border border-green-200 hover:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
              title="Edit"
            >
              <Pencil class="w-4 h-4" />
            </button>
            <button
              @click="$emit('delete-student', student._id, student.name)"
              :disabled="isLoading || isDeleting"
              class="flex items-center justify-center p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors duration-200 border border-red-200 hover:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-200"
              title="Delete"
            >
              <div
                v-if="isDeleting && deletingStudentId === student._id"
                class="w-4 h-4 border-2 border-red-600/30 border-t-red-600 rounded-full animate-spin"
              ></div>
              <Trash2
                v-else
                class="w-4 h-4"
              />
            </button>
          </div>
        </div>
        <!-- Simple Empty State -->
        <div
          v-if="!isLoading && students.length === 0"
          class="text-center py-12"
        >
          <div
            class="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md border border-blue-200"
          >
            <Users class="w-8 h-8 text-blue-600" />
          </div>
          <h3 class="text-lg font-bold text-gray-900 mb-2">
            No students found
          </h3>
          <p class="text-sm text-gray-600">
            Try adjusting your search criteria or add new students.
          </p>
        </div>
      </div>
    </div>
    <!-- Desktop View - Table (≥ 768px) -->
    <div class="hidden md:block overflow-x-auto">
      <!-- Desktop Skeleton Loading -->
      <div
        v-if="isLoading"
        class="p-4 lg:p-6"
      >
        <div class="space-y-4">
          <div
            v-for="i in 8"
            :key="i"
            class="flex items-center space-x-4 py-4 border-b border-gray-100 last:border-b-0"
          >
            <!-- Checkbox Skeleton -->
            <div class="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
            <!-- Roll Number Skeleton -->
            <div class="flex items-center space-x-3">
              <div class="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
              <div class="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
            </div>
            <!-- Student Info Skeleton -->
            <div class="flex items-center space-x-4 flex-1">
              <div
                class="w-12 h-12 bg-gray-200 rounded-full animate-pulse"
              ></div>
              <div class="space-y-2">
                <div class="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                <div class="h-3 bg-gray-200 rounded w-24 animate-pulse"></div>
              </div>
            </div>
            <!-- Contact Skeleton -->
            <div class="space-y-2">
              <div class="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
              <div class="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
            </div>
            <!-- Academic Skeleton -->
            <div class="flex items-center space-x-2">
              <div class="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
              <div class="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
            </div>
            <!-- Status Skeleton -->
            <div class="h-6 bg-gray-200 rounded-full w-20 animate-pulse"></div>
            <!-- Date Skeleton -->
            <div class="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
            <!-- Actions Skeleton -->
            <div class="flex space-x-2">
              <div class="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
              <div class="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
              <div class="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
      <!-- Actual Desktop Table -->
      <table
        v-else
        class="w-full"
      >
        <thead class="bg-gray-50 border-b border-gray-200">
          <tr>
            <th
              class="px-4 lg:px-6 py-4 lg:py-5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
            >
              <input
                type="checkbox"
                :checked="isAllSelected"
                @change="$emit('toggle-select-all')"
                :disabled="isLoading || students.length === 0"
                class="w-4 h-4 text-blue-500 bg-gray-100 border-gray-300 rounded focus:ring-blue-400 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </th>
            <th
              class="px-4 lg:px-6 py-4 lg:py-5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
            >
              <span class="inline-flex items-center gap-1">
                <ArrowUpDown
                  class="w-4 h-4 group-hover:text-blue-600 transition-colors"
                />
                <IdCard class="w-3.5 h-3.5 text-purple-600" />
                <span>Roll No</span>
              </span>
            </th>
            <th
              class="px-4 lg:px-6 py-4 lg:py-5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
            >
              <span class="inline-flex items-center gap-1">
                <span>&nbsp; &nbsp; &nbsp; &nbsp;</span>
                <ArrowUpDown
                  class="w-4 h-4 group-hover:text-blue-600 transition-colors"
                />
                <Users class="w-3.5 h-3.5 text-blue-600" />
                <span>&nbsp;Student and &nbsp;</span>
                <Mail class="w-3.5 h-3.5 text-blue-600" />
                <span>&nbsp;Email</span>
              </span>
            </th>
            <th
              class="px-4 lg:px-6 py-4 lg:py-5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
            >
              <span class="inline-flex items-center gap-2">
                <ArrowUpDown
                  class="w-4 h-4 group-hover:text-blue-600 transition-colors"
                />
                <Phone class="w-3.5 h-3.5 text-green-600" />
                <span>Contact Info</span>
              </span>
            </th>
            <th
              class="px-4 lg:px-6 py-4 lg:py-5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
            >
              <span class="inline-flex items-center gap-2">
                <School class="w-3.5 h-3.5 text-green-600" />
                <span>Class</span>
              </span>
            </th>
            <th
              class="px-4 lg:px-6 py-4 lg:py-5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
            >
              <span class="inline-flex items-center gap-2">
                <FileChartLine class="w-3.5 h-3.5 text-amber-600" />
                <span>Status</span>
              </span>
            </th>
            <th
              class="px-4 lg:px-6 py-4 lg:py-5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
            >
              <span class="inline-flex items-center gap-2">
                <Calendar class="w-3.5 h-3.5 text-blue-600" />
                <span>Enrollment</span>
              </span>
            </th>
            <th
              class="px-4 lg:px-6 py-4 lg:py-5 text-left text-xs font-bold text-gray-600 uppercase tracking-wider"
            >
              <span class="inline-flex items-center gap-2">
                <Edit class="w-3.5 h-3.5 text-blue-700" />
                <span>Actions</span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr
            v-for="student in students"
            :key="student.id"
            class="hover:bg-gray-50 transition-all duration-200"
            :class="{
              'bg-blue-50 ring-1 ring-blue-200': isStudentSelected(student._id),
            }"
          >
            <td class="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
              <input
                type="checkbox"
                :checked="isStudentSelected(student._id)"
                @change="$emit('toggle-student-selection', student._id)"
                :disabled="isLoading"
                class="w-4 h-4 text-blue-500 bg-gray-100 border-gray-300 rounded focus:ring-blue-400 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </td>
            <td class="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
              <div class="flex items-center gap-3">
                <div
                  class="p-2 bg-purple-100 rounded-lg border border-purple-200 shadow-sm"
                >
                  <IdCard class="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
                </div>
                <span class="text-sm font-bold text-gray-800">{{
                  student.rollNumber
                }}</span>
              </div>
            </td>
            <td
              class="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap cursor-pointer"
              @click="$emit('view-student', student)"
            >
              <div class="flex items-center gap-4">
                <div class="flex-shrink-0">
                  <div
                    class="h-10 w-10 lg:h-12 lg:w-12 rounded-full flex items-center justify-center border shadow-sm border-blue-200 bg-blue-100"
                  >
                    <!-- If photo exists -->
                    <img
                      v-if="student.photo"
                      :src="student.photo"
                      alt="Student Photo"
                      class="h-full w-full rounded-full object-cover"
                    />
                    <!-- Fallback to initials -->
                    <span
                      v-else
                      class="text-xs lg:text-sm font-bold text-blue-600"
                    >
                      {{ getInitials(student.name) }}
                    </span>
                  </div>
                </div>
                <div>
                  <div class="text-sm font-bold text-gray-800">
                    {{ student.name }}
                  </div>
                  <div
                    class="text-xs text-gray-500 flex items-center gap-1 mt-1"
                  >
                    <Mail class="w-3 h-3 text-gray-400" />
                    {{ student.email }}
                  </div>
                </div>
              </div>
            </td>
            <td class="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
              <div class="space-y-2">
                <div class="flex items-center gap-2 text-sm text-gray-700">
                  <div class="p-1 bg-green-50 rounded border border-green-100">
                    <Phone class="w-3 h-3 text-green-600" />
                  </div>
                  <span class="font-medium">{{ student.phone || "N/A" }}</span>
                </div>
                <div class="flex items-center gap-2 text-xs text-gray-500">
                  <div class="p-1 bg-blue-50 rounded border border-blue-100">
                    <MapPinHouse class="w-3 h-3 text-blue-600" />
                  </div>
                  <span
                    :title="student.address || student.city"
                    class="truncate max-w-24"
                  >
                    {{
                      student.address && student.address.trim()
                        ? student.address.includes(",")
                          ? student.address.split(",")[0]
                          : student.address.split(" ")[0]
                        : student.city && student.city.trim()
                        ? student.city
                        : "N/A"
                    }}
                  </span>
                </div>
              </div>
            </td>
            <td class="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
              <div class="flex items-center gap-2">
                <div
                  class="p-2 bg-green-100 rounded-lg border border-green-200 shadow-sm"
                >
                  <School class="w-3 h-3 lg:w-4 lg:h-4 text-green-600" />
                </div>
                <span class="text-[12px] font-semibold text-gray-800">
                  {{
                    typeof student.class === "object"
                      ? `${student.class?.name || ""}${
                          student.class?.section
                            ? " - " +
                              student.class.section.replace(/section\s*/i, "")
                            : ""
                        }`
                      : student.class || "N/A"
                  }}
                </span>
              </div>
            </td>
            <td class="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
              <span
                :class="getStatusClass(student.status)"
                class="inline-flex items-center gap-2 px-2 lg:px-3 py-1 lg:py-2 text-xs font-bold rounded-full border"
              >
                <FileChartLine class="w-3 h-3" />
                {{ student.status === "leaved" ? "inactive" : student.status }}
              </span>
            </td>

            <td class="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
              <div class="flex items-center gap-2 text-sm text-gray-600">
                <div class="p-1 bg-blue-50 rounded border border-blue-100">
                  <Calendar class="w-3 h-3 lg:w-4 lg:h-4 text-blue-600" />
                </div>
                <span class="font-medium">{{
                  formatDate(student.enrollmentDate) || "N/A"
                }}</span>
              </div>
            </td>
            <td class="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
              <div class="flex items-center gap-2">
                <button
                  @click="$emit('view-student', student)"
                  :disabled="isLoading"
                  class="p-2 text-blue-600 hover:text-white hover:bg-blue-600 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-blue-200 hover:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  title="View Details"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    ></path>
                  </svg>
                </button>
                <!-- <button
                  @click="$emit('edit-student', student)"
                  :disabled="isLoading"
                  class="p-2 text-green-600 hover:text-white hover:bg-green-600 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-green-200 hover:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
                  title="Edit"
                >
                  <Pencil class="w-4 h-4" />
                </button> -->
                <button
                  @click="$emit('delete-student', student._id, student.name)"
                  :disabled="isLoading || isDeleting"
                  class="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-red-200 hover:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-200"
                  title="Delete"
                >
                  <div
                    v-if="isDeleting && deletingStudentId === student._id"
                    class="w-4 h-4 border-2 border-red-600/30 border-t-red-600 rounded-full animate-spin"
                  ></div>
                  <Trash2
                    v-else
                    class="w-4 h-4"
                  />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- Desktop Empty State -->
      <div
        v-if="!isLoading && students.length === 0"
        class="text-center py-16"
      >
        <div
          class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-200 shadow-md"
        >
          <Users class="w-10 h-10 text-blue-500" />
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">No students found</h3>
        <p class="text-gray-600">
          Try adjusting your search criteria or add new students.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {
    Edit,
    School,
    Mail,
    Phone,
    MapPinHouse,
    FileChartLine,
    Calendar,
    IdCard,
    Users,
    Trash2,
    Pencil,
    ArrowUpDown,
  } from "lucide-vue-next";
  const props = defineProps({
    students: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isDeleting: {
      type: Boolean,
      default: false,
    },
    deletingStudentId: {
      type: String,
      default: null,
    },
    selectedStudents: {
      type: Array,
      default: () => [],
    },
    isAllSelected: {
      type: Boolean,
      default: false,
    },
    tableLoadingMessage: {
      type: String,
      default: "Loading...",
    },
  });
  defineEmits([
    "toggle-select-all",
    "toggle-student-selection",
    "view-student",
    "edit-student",
    "delete-student",
  ]);
  // Utility functions
  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "N/A";
  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "NA";
  const getStatusClass = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border-green-300";
      case "leaved":
        return "bg-red-100 text-red-800 border-red-300";
      case "passout":
        return "bg-blue-100 text-blue-800 border-blue-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };
  const isStudentSelected = (studentId) => {
    return props.selectedStudents?.includes(studentId) || false;
  };
</script>

<style scoped>
  .touch-manipulation {
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
</style>
